import { useState } from 'react';
import { BrandMark } from '../components/BrandMark';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { ErrorNotice, ThemeToggle } from '../components/ui';

/** MockCheckoutPage stands in for Stripe's own hosted checkout page when no
 * real Stripe account is configured — see internal/stripeclient's package
 * doc comment. Completing it calls the exact same billing/entitlement
 * logic a real Stripe webhook would; only the transport differs. This page
 * is clearly labeled as a sandbox so nobody mistakes it for a real
 * payment. */
export function MockCheckoutPage() {
  const [searchParams] = useSearchParams();
  const customerEmail = searchParams.get('customer_email') ?? '';
  const clientReferenceId = searchParams.get('client_reference_id') ?? '';
  const successUrl = searchParams.get('success_url') ?? '/app/billing';
  const cancelUrl = searchParams.get('cancel_url') ?? '/app/billing';
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function complete() {
    setSubmitting(true);
    setError(null);
    try {
      await api.completeMockCheckout(customerEmail, clientReferenceId);
      navigateExternalOrInternal(successUrl, navigate);
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setSubmitting(false);
    }
  }

  function cancel() {
    navigateExternalOrInternal(cancelUrl, navigate);
  }

  return (
    <div className="auth-shell">
      <div className="auth-shell__theme">
        <ThemeToggle />
      </div>
      <div className="auth-card">
        <Link to="/" className="auth-card__brand">
          <BrandMark size={26} />
          Commerce Ops
        </Link>
        <div className="badge badge--neutral mock-checkout__badge">Sandbox checkout — no real payment</div>
        <h1>Subscribe to Commerce Ops</h1>
        <p className="muted">
          No Stripe account is configured for this deployment, so this stands in for Stripe's own checkout page.
          Completing it runs the exact same subscription and account-provisioning logic a real payment would.
        </p>
        <div className="mock-checkout__summary">
          <div>
            <span className="muted">Email</span>
            <strong>{customerEmail || '(none provided)'}</strong>
          </div>
          <div>
            <span className="muted">Plan</span>
            <strong>Commerce Ops — $49/month (mock)</strong>
          </div>
        </div>
        <ErrorNotice error={error} />
        <div className="mock-checkout__actions">
          <button className="button" type="button" onClick={complete} disabled={submitting || !customerEmail}>
            {submitting ? 'Processing' : 'Complete mock payment'}
          </button>
          <button className="button button--secondary" type="button" onClick={cancel} disabled={submitting}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function navigateExternalOrInternal(url: string, navigate: ReturnType<typeof useNavigate>) {
  try {
    const target = new URL(url, window.location.origin);
    if (target.origin === window.location.origin) {
      navigate(target.pathname + target.search, { replace: true });
      return;
    }
  } catch {
    // fall through to a relative internal path
  }
  navigate(url, { replace: true });
}
